/**
 * Quantity input with Shopify B2B quantity rules (min / increment / max).
 * Replaces the stock QuantityInput from main.js, so it must load before main.js.
 */
class EmQuantityInput extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('.qty-input__input');
    if (!this.input) return;

    this.currentQty = this.input.value;
    this.changeEvent = new Event('change', { bubbles: true });
    this.boundVariantChange = this.handleVariantChange.bind(this);

    this.addEventListener('click', this.handleClick.bind(this));
    this.input.addEventListener('focus', EmQuantityInput.handleFocus);
    this.input.addEventListener('keydown', this.handleKeydown.bind(this));
    this.input.addEventListener('change', this.handleChange.bind(this));

    this.updateButtons();
  }

  connectedCallback() {
    if (!this.input || this.isCart()) return;

    this.productContainer = this.closest('.js-product');
    if (this.productContainer) {
      this.productContainer.addEventListener('on:variant:change', this.boundVariantChange);
    }
  }

  disconnectedCallback() {
    if (this.productContainer) {
      this.productContainer.removeEventListener('on:variant:change', this.boundVariantChange);
      this.productContainer = null;
    }
  }

  isCart() {
    return this.input.name === 'updates[]';
  }

  /**
   * Quantity 0 is allowed in the cart (removes the line) and in quick order.
   * @returns {boolean}
   */
  allowsZero() {
    return this.isCart() || this.input.getAttribute('min') === '0';
  }

  /**
   * @returns {{ min: number, increment: number, max: number }}
   */
  getRules() {
    const min = Number(this.input.dataset.min);
    const increment = Number(this.input.dataset.increment);
    const max = this.input.dataset.max ? Number(this.input.dataset.max) : Infinity;

    return {
      min: min >= 1 ? min : 1,
      increment: increment >= 1 ? increment : 1,
      max: Number.isFinite(max) ? max : Infinity
    };
  }

  /**
   * Rounds a quantity up to the next valid step and clamps it to the max.
   * @param {number|string} value - Requested quantity.
   * @returns {number}
   */
  normalizeQuantity(value) {
    const { min, increment, max } = this.getRules();
    let quantity = Number(value);

    if (!Number.isFinite(quantity)) quantity = this.allowsZero() ? 0 : min;
    if (this.allowsZero() && quantity <= 0) return 0;

    quantity = Math.max(quantity, min);
    quantity = min + Math.ceil((quantity - min) / increment) * increment;

    if (quantity > max) {
      const maxSteps = Math.floor((max - min) / increment);
      if (maxSteps < 0) return this.allowsZero() ? 0 : min;
      quantity = min + maxSteps * increment;
    }

    return quantity;
  }

  /**
   * Handles clicks on the plus / minus buttons.
   * @param {object} evt - Event object.
   */
  handleClick(evt) {
    const button = evt.target.closest('.qty-input__btn');
    if (!button || !this.contains(button)) return;

    evt.preventDefault();
    if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;

    const { min, increment, max } = this.getRules();
    let quantity = Number(this.input.value) || 0;

    if (button.name === 'plus') {
      quantity = quantity < min
        ? min
        : min + (Math.floor((quantity - min) / increment) + 1) * increment;

      if (quantity > max) {
        this.updateButtons();
        return;
      }
    } else if (button.name === 'minus') {
      if (quantity <= min) {
        quantity = this.allowsZero() ? 0 : min;
      } else {
        quantity = min + Math.max(0, Math.ceil((quantity - min) / increment) - 1) * increment;
      }
    } else {
      return;
    }

    this.setQuantity(this.normalizeQuantity(quantity));
  }

  /**
   * Sets the value and fires a 'change' event if it differs from the current value.
   * @param {number} quantity - New quantity.
   */
  setQuantity(quantity) {
    const newValue = String(quantity);
    const changed = this.input.value !== newValue;

    this.input.value = newValue;
    this.currentQty = newValue;
    this.updateButtons();

    if (changed) this.input.dispatchEvent(this.changeEvent);
  }

  /**
   * Corrects typed values. Runs on the input itself, so the cart sees the corrected value.
   */
  handleChange() {
    const newValue = String(this.normalizeQuantity(this.input.value));
    if (this.input.value !== newValue) this.input.value = newValue;

    this.currentQty = this.input.value;
    this.updateButtons();
  }

  /**
   * Applies the quantity rules of the newly selected variant.
   * @param {object} evt - 'on:variant:change' event.
   */
  handleVariantChange(evt) {
    const variant = evt.detail && evt.detail.variant;
    if (!variant) return;

    const rule = this.getVariantRule(variant);
    const previous = this.getRules();
    const min = Math.max(1, Number(rule.min) || 1);
    const increment = Math.max(1, Number(rule.increment) || 1);
    const max = rule.max !== null && rule.max !== undefined && rule.max !== ''
      ? Number(rule.max)
      : null;

    this.input.dataset.min = String(min);
    this.input.dataset.increment = String(increment);
    this.input.step = String(increment);
    if (this.input.getAttribute('min') !== '0') this.input.min = String(min);

    if (max !== null && Number.isFinite(max)) {
      this.input.dataset.max = String(max);
      this.input.max = String(max);
    } else {
      delete this.input.dataset.max;
      this.input.removeAttribute('max');
    }

    const rulesChanged = previous.min !== min
      || previous.increment !== increment
      || previous.max !== (max === null ? Infinity : max);

    const quantity = rulesChanged && !this.allowsZero()
      ? min
      : this.normalizeQuantity(this.input.value);

    this.input.value = String(quantity);
    this.currentQty = this.input.value;
    this.updateButtons();
  }

  /**
   * @param {object} variant - Variant object from the variant picker.
   * @returns {{ min: number, increment: number, max: ?number }}
   */
  getVariantRule(variant) {
    if (!this.variantRules) {
      const dataEl = this.querySelector('.js-em-quantity-rules');
      try {
        this.variantRules = dataEl ? JSON.parse(dataEl.textContent) : {};
      } catch (e) {
        this.variantRules = {};
      }
    }

    return this.variantRules[variant.id]
      || variant.quantity_rule
      || { min: 1, increment: 1, max: null };
  }

  updateButtons() {
    const { min, increment, max } = this.getRules();
    const quantity = Number(this.input.value) || 0;
    const minusButton = this.querySelector('[name="minus"]');
    const plusButton = this.querySelector('[name="plus"]');

    if (minusButton) {
      EmQuantityInput.setButtonState(
        minusButton,
        this.allowsZero() ? quantity <= 0 : quantity <= min
      );
    }

    if (plusButton) {
      const next = quantity < min ? min : quantity + increment;
      EmQuantityInput.setButtonState(plusButton, next > max);
    }
  }

  /**
   * Uses aria-disabled instead of the disabled attribute, because product.css locks the whole
   * quantity input as soon as any button inside .product-info__add-to-cart is [disabled].
   * @param {Element} button - Plus or minus button.
   * @param {boolean} isDisabled - Whether the button should be inactive.
   */
  static setButtonState(button, isDisabled) {
    button.disabled = false;
    button.setAttribute('aria-disabled', String(isDisabled));
  }

  /**
   * @param {object} evt - Event object.
   */
  static handleFocus(evt) {
    if (window.matchMedia('(pointer: fine)').matches) {
      evt.target.select();
    }
  }

  /**
   * @param {object} evt - Event object.
   */
  handleKeydown(evt) {
    if (evt.key !== 'Enter') return;
    evt.preventDefault();

    const newValue = String(this.normalizeQuantity(this.input.value));
    this.input.value = newValue;

    if (newValue !== this.currentQty) {
      this.currentQty = newValue;
      this.input.dispatchEvent(this.changeEvent);
    }

    this.updateButtons();
  }
}

if (!customElements.get('quantity-input')) {
  customElements.define('quantity-input', EmQuantityInput);
}
